import { Flame, Pause, Play, Snowflake } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { SolutionsBeaker3D, type SolutionsSceneState } from './SolutionsBeaker3D';
import { SolutionBeaker } from './SolutionsCrystalObservation';
import { SolubilityGraph } from './SolutionsSolubilityGraph';
import { formatModelNumber } from './modelInputContract';
import {
  CRYSTAL_CHALLENGES,
  CRYSTAL_FIELDS,
  CRYSTAL_QUESTION,
  type CrystalExploreResult,
  type CrystalSetup,
  DEFAULT_CRYSTAL_SETUP,
  SOLUTIONS_BEAKER_CAPACITY_G,
  calculateCrystallisation,
} from './solutionsExplore';
import { SOLUBILITY_PRESETS, type SolutePreset } from './solutionsLab';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

type SoluteId = SolutePreset['id'];
type Stage = 'hot' | 'cooled';

const CRYSTAL_PRESETS: { id: string; label: string; soluteId: SoluteId; setup: CrystalSetup }[] = [
  { id: 'classic', label: '80 g KNO₃ in 100 g water, 60 → 20 °C', soluteId: 'potassium-nitrate', setup: DEFAULT_CRYSTAL_SETUP },
  { id: 'salt', label: '38 g NaCl in 100 g water, 80 → 20 °C', soluteId: 'sodium-chloride', setup: { waterMassG: 100, soluteMassG: 38, hotTemperatureC: 80, coolTemperatureC: 20 } },
  { id: 'too-much', label: '300 g KNO₃ in 50 g water at 40 °C', soluteId: 'potassium-nitrate', setup: { waterMassG: 50, soluteMassG: 300, hotTemperatureC: 40, coolTemperatureC: 20 } },
  { id: 'big-batch', label: '300 g KNO₃ in 500 g water, 60 → 0 °C', soluteId: 'potassium-nitrate', setup: { waterMassG: 500, soluteMassG: 300, hotTemperatureC: 60, coolTemperatureC: 0 } },
];

const MAX_DOTS = 24;
const MAX_CRYSTAL_MARKS = 12;
const GRAMS_PER_DOT = 10;

const presetFor = (id: SoluteId) => SOLUBILITY_PRESETS.find((candidate) => candidate.id === id) ?? SOLUBILITY_PRESETS[0];

/** 2D particle beakers: dots per 100 g water follow the dissolved mass; diamonds follow solid mass. */
function particleCounts(result: CrystalExploreResult, waterMassG: number) {
  const dots = (dissolvedG: number) => Math.max(1, Math.min(MAX_DOTS, Math.round((dissolvedG * 100) / waterMassG / GRAMS_PER_DOT)));
  const marks = (solidG: number) => (solidG > 0.05 ? Math.min(MAX_CRYSTAL_MARKS, Math.ceil(solidG / GRAMS_PER_DOT)) : 0);
  const { model, undissolvedHotG } = result;
  return {
    hot: { dissolved: dots(model.dissolvedHotG), solid: marks(undissolvedHotG) },
    cooled: { dissolved: dots(model.dissolvedHotG - model.crystalsOnCoolingG), solid: marks(model.crystalsOnCoolingG + undissolvedHotG) },
  };
}

/**
 * Year 10 Explore: the learner picks a reviewed solute and types water mass, solute mass and the
 * hot and cool temperatures. One result from `calculateCrystallisation` drives the graph, the 3D
 * beaker, the particle beakers, the readouts and the worked calculation.
 */
export function CrystalExplore() {
  const [soluteId, setSoluteId] = useState<SoluteId>(SOLUBILITY_PRESETS[0].id);
  const [setup, setSetup] = useState<CrystalSetup>(DEFAULT_CRYSTAL_SETUP);
  const [validSetup, setValidSetup] = useState<CrystalSetup>(DEFAULT_CRYSTAL_SETUP);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('cooled');
  const reducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState<boolean | null>(null);
  const moving = playing ?? !reducedMotion;

  const preset = presetFor(soluteId);
  const result = useMemo(() => calculateCrystallisation(preset, validSetup), [preset, validSetup]);
  const { model } = result;

  const apply = (next: CrystalSetup, nextSolute: SoluteId = soluteId) => {
    setSetup(next);
    setSoluteId(nextSolute);
    try {
      calculateCrystallisation(presetFor(nextSolute), next);
      setValidSetup(next);
      setSetupError(null);
    } catch (error) {
      setSetupError(error instanceof Error ? error.message : String(error));
    }
  };

  const hot = stage === 'hot';
  const temperatureC = hot ? validSetup.hotTemperatureC : validSetup.coolTemperatureC;
  const sceneState: SolutionsSceneState = {
    // Every reviewed curve today is a colourless salt; a copper(II) curve would use 'blue'.
    appearance: 'colourless',
    waterFill: validSetup.waterMassG / SOLUTIONS_BEAKER_CAPACITY_G,
    temperatureC,
    heating: hot,
    crystalsG: hot ? 0 : model.crystalsOnCoolingG,
    undissolvedG: result.undissolvedHotG,
  };
  const solidG = sceneState.crystalsG + sceneState.undissolvedG;
  const counts = particleCounts(result, validSetup.waterMassG);
  const g = (value: number) => `${formatModelNumber(value, 1)} g`;

  return (
    <article className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Explore crystallisation</p>
          <h3 className="mt-2 text-2xl font-extrabold text-ink">Make your own hot solution, then cool it</h3>
          <p className="mt-1 text-sm font-medium text-slate2">
            Choose a solute and type the amounts and temperatures. The model uses the reviewed solubility curve (0–100 °C) and
            does not model supersaturation or evaporation.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPlaying(!moving)}
          className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
        >
          {moving ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
          {moving ? 'Pause view' : 'Play view'}
        </button>
      </div>

      <div className="mt-5 grid min-w-0 gap-5 lg:grid-cols-2">
        <div className="min-w-0">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4" role="group" aria-label="Choose the solute">
            {SOLUBILITY_PRESETS.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                aria-pressed={candidate.id === preset.id}
                onClick={() => apply(setup, candidate.id)}
                className={`rounded-2xl border p-3 text-left ${candidate.id === preset.id ? 'border-brand-sky bg-wash-sky' : 'border-hairline bg-white'}`}
              >
                <span className="block text-sm font-extrabold text-ink">{candidate.formula}</span>
                <span className="mt-1 block text-xs font-medium text-slate2">{candidate.name}</span>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <ModelInputPanel
              legend="Set up the solution"
              fields={CRYSTAL_FIELDS}
              values={setup}
              onChange={(next) => apply(next as CrystalSetup)}
              sliderKey="coolTemperatureC"
            />
          </div>
          {setupError ? (
            <p role="alert" className="mt-2 rounded-2xl bg-wash-coral px-3 py-2 text-xs font-bold text-ink">
              {setupError} Showing the last valid setup.
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Crystallisation presets">
            {CRYSTAL_PRESETS.map((shortcut) => (
              <button
                key={shortcut.id}
                type="button"
                onClick={() => apply(shortcut.setup, shortcut.soluteId)}
                className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50"
              >
                {shortcut.label}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <ExploreChallenges
              question={CRYSTAL_QUESTION}
              challenges={CRYSTAL_CHALLENGES}
              result={{ soluteId, setup: validSetup, result }}
            />
          </div>
          <div className="mt-4 min-w-0 rounded-[1.75rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4">
            <p className="text-xs font-extrabold uppercase tracking-[0.13em] text-brand-sky">Solubility curve · {preset.name}</p>
            <div className="mt-2 overflow-x-auto">
              <SolubilityGraph
                preset={preset}
                hotTemperature={validSetup.hotTemperatureC}
                coolTemperature={validSetup.coolTemperatureC}
                loadPer100g={result.loadPer100gWater}
                limits={{ hot: result.hot.value, cool: result.cool.value }}
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs font-bold text-slate2">
              <span>● yellow = hot</span>
              <span>● green = cooled</span>
              <span>○ ring = most that dissolves</span>
              <span>- - your load = {formatModelNumber(result.loadPer100gWater, 1)} g / 100 g water</span>
            </div>
            <p className="mt-2 text-xs font-medium leading-5 text-slate2">{preset.boundary}</p>
          </div>
        </div>

        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">What you see</p>
            <div className="flex gap-2" role="group" aria-label="Choose the stage">
              <button
                type="button"
                aria-pressed={hot}
                onClick={() => setStage('hot')}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-extrabold ${hot ? 'border-brand-coral bg-wash-coral text-ink' : 'border-hairline bg-white text-ink-soft'}`}
              >
                <Flame className="h-3.5 w-3.5" aria-hidden="true" /> Hot solution
              </button>
              <button
                type="button"
                aria-pressed={!hot}
                onClick={() => setStage('cooled')}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-extrabold ${!hot ? 'border-brand-mint bg-wash-mint text-ink' : 'border-hairline bg-white text-ink-soft'}`}
              >
                <Snowflake className="h-3.5 w-3.5" aria-hidden="true" /> After cooling
              </button>
            </div>
          </div>
          <SolutionsBeaker3D
            state={sceneState}
            playing={moving}
            label={`Beaker of ${g(validSetup.waterMassG)} water with ${g(validSetup.soluteMassG)} ${preset.formula} at ${formatModelNumber(temperatureC, 1)} °C: ${
              solidG > 0.05 ? `${g(solidG)} of solid at the bottom` : 'no solid, all dissolved'
            }`}
          />
          <div className="mt-4 grid grid-cols-2 gap-2">
            <SolutionBeaker
              label="Hot solution"
              temperature={validSetup.hotTemperatureC}
              dissolvedDots={counts.hot.dissolved}
              crystalDots={counts.hot.solid}
            />
            <SolutionBeaker
              label="After cooling"
              temperature={validSetup.coolTemperatureC}
              dissolvedDots={counts.cooled.dissolved}
              crystalDots={counts.cooled.solid}
            />
          </div>
          <p className="mt-2 text-center text-xs font-medium leading-5 text-slate2">
            Zoomed in: one dot ≈ {GRAMS_PER_DOT} g dissolved per 100 g water; one diamond ≈ {GRAMS_PER_DOT} g of solid. Not to scale.
          </p>
        </div>
      </div>

      <div className="mt-5 grid min-w-0 gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <WorkedStepsPanel
          title="How the model works it out"
          steps={result.steps}
          stepTestIdPrefix="crystal-step"
          result={result.result}
          resultTestId="crystal-result"
          limitNote={result.limitNote}
        />
        <dl className="grid content-start gap-3 sm:grid-cols-2" aria-label="Crystallisation readout">
          {[
            ['Hot limit', g(model.hotLimitG)],
            ['Cool limit', g(model.coolLimitG)],
            ['Dissolved when hot', g(model.dissolvedHotG)],
            ['Crystals on cooling', g(model.crystalsOnCoolingG)],
            ['Never dissolved', g(result.undissolvedHotG)],
            ['Solid after cooling', g(model.finalSolidG)],
          ].map(([term, value]) => (
            <div key={term} className="rounded-2xl border border-hairline bg-surface p-3">
              <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">{term}</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </article>
  );
}
